import {
  Root as RadixCheckbox,
  Indicator as RadixCheckboxIndicator,
  type CheckboxProps as RadixCheckboxProps,
} from "@radix-ui/react-checkbox";
import { forwardRef } from "react";

import { checkboxVariants, type CheckboxVariants } from "./checkbox.styles";
import { Icon } from "./icon";
import { formatAriaInvalid } from "./style.utils";

type CheckboxRef = React.ComponentRef<typeof RadixCheckbox>;

export interface CheckboxProps
  extends
    Omit<RadixCheckboxProps, "aria-invalid">,
    Omit<
      CheckboxVariants,
      "checked" | "disabled" | "indeterminate" | "invalid"
    > {
  "aria-invalid"?: boolean | "true" | "false" | undefined;
  name: string;
}

const Checkbox = forwardRef<CheckboxRef, CheckboxProps>(
  (
    { "aria-invalid": ariaInvalid, checked, className, disabled, ...props },
    ref,
  ) => {
    const indeterminate = checked === "indeterminate";
    const iconName = indeterminate
      ? "custom-checkbox-indeterminate"
      : "custom-checkbox-checked";
    return (
      <RadixCheckbox
        {...props}
        checked={checked}
        data-invalid={formatAriaInvalid(ariaInvalid)}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        className={checkboxVariants({
          className,
          disabled,
          invalid: formatAriaInvalid(ariaInvalid),
        })}
        ref={ref}
      >
        <RadixCheckboxIndicator className="size-full">
          <Icon name={iconName} className="size-full" />
        </RadixCheckboxIndicator>
      </RadixCheckbox>
    );
  },
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
